// 路由跳转高亮指令
const routeHighlight = {
  // 全局配置
  config: {
    globalIgnoreClasses: ['h_title','lilili'] // 全局忽略类名数组
  },
  
  // 安装方法，用于设置全局配置
  install(app, options = {}) {
    if (options.globalIgnoreClasses) {
      this.config.globalIgnoreClasses = options.globalIgnoreClasses
    }
    
    // 注册指令
    app.directive('routeHighlight', this)
  },
  mounted(el, binding) {
    const applyHighlight = (element, configs) => {
      if (configs.length === 0) return
      
      const processTextNodes = (element, config) => {
        element.childNodes.forEach(node => {
          if (node.nodeType === Node.TEXT_NODE) {
            let text = node.textContent
            if (!text) return
            
            const targetText = config.targetText
            const index = text.indexOf(targetText)
            
            if (index !== -1) {
              // 检查父元素是否在忽略类名列表中（支持全局配置和单个配置）
              let parentElement = node.parentNode
              let shouldSkip = false
              
              // 合并全局忽略类名和单个配置的忽略类名
              const globalIgnoreClasses = routeHighlight.config.globalIgnoreClasses || []
              const configIgnoreClasses = config.ignoreClasses || []
              const allIgnoreClasses = [...globalIgnoreClasses, ...configIgnoreClasses]
              
              // 遍历父元素链，检查是否有忽略类名
              while (parentElement && parentElement !== el) {
                if (parentElement.classList && allIgnoreClasses.length > 0) {
                  for (const className of allIgnoreClasses) {
                    if (parentElement.classList.contains(className)) {
                      shouldSkip = true
                      break
                    }
                  }
                  if (shouldSkip) break
                }
                parentElement = parentElement.parentNode
              }
              
              if (shouldSkip) return
              
              const fragment = document.createDocumentFragment()
              
              // 添加目标文本之前的内容
              if (index > 0) {
                fragment.appendChild(document.createTextNode(text.substring(0, index)))
              }
              
              // 创建高亮元素
              const highlightSpan = document.createElement('span')
              highlightSpan.textContent = targetText
              highlightSpan.className = config.className || 'route-highlight'
              
              // 设置数据属性用于标识
              highlightSpan.setAttribute('data-route-highlight', 'true')
              highlightSpan.setAttribute('data-route-path', config.routePath || '#')
              
              // 添加点击事件
              if (config.clickable !== false && config.routePath) {
                highlightSpan.style.cursor = 'pointer'
                highlightSpan.addEventListener('click', (e) => {
                  e.stopPropagation()
                  // 触发自定义事件，由父组件处理路由跳转
                  const event = new CustomEvent('route-highlight-click', {
                    bubbles: true,
                    detail: { routePath: config.routePath }
                  })
                  el.dispatchEvent(event)
                })
              }
              
              fragment.appendChild(highlightSpan)
              
              // 添加目标文本之后的内容
              const afterIndex = index + targetText.length
              if (afterIndex < text.length) {
                fragment.appendChild(document.createTextNode(text.substring(afterIndex)))
              }
              
              // 替换原文本节点
              node.parentNode.replaceChild(fragment, node)
            }
          } else if (node.nodeType === Node.ELEMENT_NODE && !node.hasAttribute('data-route-highlight')) {
            // 递归处理子元素，但避免重复处理已高亮的元素
            processTextNodes(node, config)
          }
        })
      }
      
      // 为每个配置项处理文本节点
      configs.forEach(config => {
        if (config && config.targetText) {
          processTextNodes(element, config)
        }
      })
    }
    
    const { value } = binding
    // 支持数组和单个对象两种形式
    const configs = Array.isArray(value) ? value : (value ? [value] : [])
    
    // 初始应用高亮
    applyHighlight(el, configs)
    
    // 使用防抖的重新高亮函数
    const debouncedReapply = debounce(() => {
      // 移除之前的高亮元素
      const highlightElements = el.querySelectorAll('[data-route-highlight]')
      highlightElements.forEach(element => {
        const text = element.textContent
        const parent = element.parentNode
        if (parent) {
          const textNode = document.createTextNode(text)
          parent.replaceChild(textNode, element)
        }
      })
      
      // 重新应用高亮
      applyHighlight(el, configs)
    }, 300)
    
    // 使用更智能的 MutationObserver，只监听特定变化
    if (configs.length > 0) {
      const observer = new MutationObserver((mutations) => {
        let hasRelevantChange = false
        
        mutations.forEach(mutation => {
          if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
            // 检查新增的节点是否包含文本内容
            const hasTextContent = Array.from(mutation.addedNodes).some(node => {
              if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) {
                return true
              }
              if (node.nodeType === Node.ELEMENT_NODE) {
                return node.textContent?.trim()
              }
              return false
            })
            
            if (hasTextContent) {
              hasRelevantChange = true
            }
          }
        })
        
        if (hasRelevantChange) {
          debouncedReapply()
        }
      })
      
      // 开始观察DOM变化，使用更保守的配置
      observer.observe(el, {
        childList: true,
        subtree: true,
        characterData: false,
        attributes: false
      })
      
      // 保存observer实例以便在unmounted时清理
      el._routeHighlightObserver = observer
      el._routeHighlightDebouncedReapply = debouncedReapply
    }
  },
  
  updated(el, binding) {
    // 指令更新时重新处理
    const { value } = binding
    const configs = Array.isArray(value) ? value : (value ? [value] : [])
    
    if (configs.length === 0) return
    
    // 移除之前添加的高亮元素
    const highlightElements = el.querySelectorAll('[data-route-highlight]')
    highlightElements.forEach(element => {
      const text = element.textContent
      const parent = element.parentNode
      if (parent) {
        const textNode = document.createTextNode(text)
        parent.replaceChild(textNode, element)
      }
    })
    
    // 重新应用高亮 - 直接调用 mounted 的逻辑
    const applyHighlight = (element, configs) => {
      if (configs.length === 0) return
      
      const processTextNodes = (element, config) => {
        element.childNodes.forEach(node => {
          if (node.nodeType === Node.TEXT_NODE) {
            let text = node.textContent
            if (!text) return
            
            const targetText = config.targetText
            const index = text.indexOf(targetText)
            
            if (index !== -1) {
              // 检查父元素是否在忽略类名列表中（支持全局配置和单个配置）
              let parentElement = node.parentNode
              let shouldSkip = false
              
              // 合并全局忽略类名和单个配置的忽略类名
              const globalIgnoreClasses = routeHighlight.config.globalIgnoreClasses || []
              const configIgnoreClasses = config.ignoreClasses || []
              const allIgnoreClasses = [...globalIgnoreClasses, ...configIgnoreClasses]
              
              // 遍历父元素链，检查是否有忽略类名
              while (parentElement && parentElement !== el) {
                if (parentElement.classList && allIgnoreClasses.length > 0) {
                  for (const className of allIgnoreClasses) {
                    if (parentElement.classList.contains(className)) {
                      shouldSkip = true
                      break
                    }
                  }
                  if (shouldSkip) break
                }
                parentElement = parentElement.parentNode
              }
              
              if (shouldSkip) return
              
              const fragment = document.createDocumentFragment()
              
              // 添加目标文本之前的内容
              if (index > 0) {
                fragment.appendChild(document.createTextNode(text.substring(0, index)))
              }
              
              // 创建高亮元素
              const highlightSpan = document.createElement('span')
              highlightSpan.textContent = targetText
              highlightSpan.className = config.className || 'route-highlight'
              
              // 设置数据属性用于标识
              highlightSpan.setAttribute('data-route-highlight', 'true')
              highlightSpan.setAttribute('data-route-path', config.routePath || '#')
              
              // 添加点击事件
              if (config.clickable !== false && config.routePath) {
                highlightSpan.style.cursor = 'pointer'
                highlightSpan.addEventListener('click', (e) => {
                  e.stopPropagation()
                  // 触发自定义事件，由父组件处理路由跳转
                  const event = new CustomEvent('route-highlight-click', {
                    bubbles: true,
                    detail: { routePath: config.routePath }
                  })
                  el.dispatchEvent(event)
                })
              }
              
              fragment.appendChild(highlightSpan)
              
              // 添加目标文本之后的内容
              const afterIndex = index + targetText.length
              if (afterIndex < text.length) {
                fragment.appendChild(document.createTextNode(text.substring(afterIndex)))
              }
              
              // 替换原文本节点
              node.parentNode.replaceChild(fragment, node)
            }
          } else if (node.nodeType === Node.ELEMENT_NODE && !node.hasAttribute('data-route-highlight')) {
            // 递归处理子元素，但避免重复处理已高亮的元素
            processTextNodes(node, config)
          }
        })
      }
      
      // 为每个配置项处理文本节点
      configs.forEach(config => {
        if (config && config.targetText) {
          processTextNodes(element, config)
        }
      })
    }
    
    applyHighlight(el, configs)
  },
  
  unmounted(el) {
    // 清理MutationObserver和防抖函数
    if (el._routeHighlightObserver) {
      el._routeHighlightObserver.disconnect()
      delete el._routeHighlightObserver
    }
    
    if (el._routeHighlightDebouncedReapply) {
      el._routeHighlightDebouncedReapply.cancel?.()
      delete el._routeHighlightDebouncedReapply
    }
  }
}

// 防抖函数
function debounce(func, wait) {
  let timeout
  const debounced = function() {
    const context = this
    const args = arguments
    clearTimeout(timeout)
    timeout = setTimeout(() => func.apply(context, args), wait)
  }
  
  debounced.cancel = function() {
    clearTimeout(timeout)
  }
  
  return debounced
}

// 导出指令对象，支持插件安装模式和直接使用
export default routeHighlight

// 同时导出安装函数，便于作为插件使用
export { routeHighlight }